import { supabase } from 'utils/supabaseClient';
import { AWARDS } from './awardsConfig';

export async function checkAndGrantAwards(userId) {

    if (!userId) return null;

    try {
        // 1. Fetch User (for current awards)
        const { data: user, error: userError } = await supabase
            .from('users')
            .select('awards')
            .eq('id', userId)
            .single();

        if (userError || !user) {
            console.error('Error fetching user for awards:', userError);
            return null;
        }

        // 2. Fetch Stats for Challenge Calculations
        // We need to know:
        // - Total unique challenges (unit_num, concept_num, lesson_num)
        // - Best daily unique challenges
        // - Challenge completion dates (for time-based awards)
        // - Multiple attempt challenges (for persistence award)
        const { data: statsData, error: statsError } = await supabase
            .from('user_stats')
            .select('unit_num, concept_num, lesson_num, timestamp, is_correct')
            .eq('user_id', userId);

        if (statsError) {
            console.error('Error fetching stats for awards:', statsError);
            return null;
        }

        // --- Calculate Challenge Stats ---
        const uniqueChallengesSet = new Set();
        const dailyUniqueChallenges = {}; // Map: "YYYY-MM-DD" -> Set(unique_challenge_ids)
        const challengeCompletionDates = []; // Array of ISO timestamp strings for correct attempts
        const challengeAttemptCounts = {}; // Map: challenge_id -> { attempts: number, completed: boolean }

        statsData?.forEach(stat => {
            const challengeId = `${stat.unit_num}-${stat.concept_num}-${stat.lesson_num}`;

            // Track unique challenges and daily counts (only correct attempts)
            if (stat.is_correct) {
                uniqueChallengesSet.add(challengeId);

                // For daily max
                const date = new Date(stat.timestamp).toLocaleDateString('sv-SE'); // ISO-like date string
                if (!dailyUniqueChallenges[date]) {
                    dailyUniqueChallenges[date] = new Set();
                }
                dailyUniqueChallenges[date].add(challengeId);

                // For time-based awards (night owl, early bird)
                challengeCompletionDates.push(stat.timestamp);
            }

            // Track attempt counts for persistence award
            if (!challengeAttemptCounts[challengeId]) {
                challengeAttemptCounts[challengeId] = { attempts: 0, completed: false };
            }
            challengeAttemptCounts[challengeId].attempts++;
            if (stat.is_correct && !challengeAttemptCounts[challengeId].completed) {
                challengeAttemptCounts[challengeId].completed = true;
            }
        });

        const totalChallenges = uniqueChallengesSet.size;

        let bestDailyChallenges = 0;
        Object.values(dailyUniqueChallenges).forEach(set => {
            if (set.size > bestDailyChallenges) {
                bestDailyChallenges = set.size;
            }
        });

        // Calculate challenges with multiple attempts before completion
        let challengesWithMultipleAttempts = 0;
        Object.values(challengeAttemptCounts).forEach(({ attempts, completed }) => {
            // Only count if completed and took more than 1 attempt
            if (completed && attempts > 1) {
                challengesWithMultipleAttempts++;
            }
        });


        // 3. Fetch Daily Progress for Best Streak
        // We need to find the longest consecutive sequence of days.
        const { data: dailyData, error: dailyError } = await supabase
            .from('user_daily_progress')
            .select('date')
            .eq('user_id', userId)
            .gt('lessons_done', 0) // Only count days where something was done? Or just existence? Usually existence implies done > 0 if we only insert on done.
            .order('date', { ascending: true }); // Ascending for easier streak calc

        let bestStreak = 0;
        let currentStreakCalc = 0;
        let lastDate = null;

        if (!dailyError && dailyData) {
            dailyData.forEach(entry => {
                const currentDate = new Date(entry.date);

                if (!lastDate) {
                    currentStreakCalc = 1;
                } else {
                    const diffTime = Math.abs(currentDate - lastDate);
                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                    if (diffDays === 1) {
                        // Consecutive day
                        currentStreakCalc++;
                    } else if (diffDays > 1) {
                        // Streak broken
                        currentStreakCalc = 1;
                    }
                    // If diffDays === 0 (same day), ignore
                }

                if (currentStreakCalc > bestStreak) {
                    bestStreak = currentStreakCalc;
                }
                lastDate = currentDate;
            });
        }

        // Also calculate unique concepts for those awards
        // We can reuse the statsData for this since it has concept_num
        const uniqueConceptsSet = new Set();
        statsData?.forEach(stat => {
            // Only count concepts from correct attempts
            if (stat.is_correct) {
                uniqueConceptsSet.add(`${stat.unit_num}-${stat.concept_num}`);
            }
        });
        const uniqueConcepts = uniqueConceptsSet.size;


        const currentStats = {
            totalChallenges,
            bestDailyChallenges,
            bestStreak,
            uniqueConcepts,
            challengeCompletionDates,
            challengesWithMultipleAttempts
        };

        // console.log("Award Stats Calculated:", currentStats);

        // 4. Check Awards
        const currentAwards = user.awards || [];

        // Filter out awards that no longer exist in config
        const validConfigIds = new Set(AWARDS.map(a => a.id));
        // Strip icon from existing awards if present, to clean up DB
        const validCurrentAwards = currentAwards
            .filter(a => validConfigIds.has(a.id))
            .map(({ icon, ...rest }) => rest);

        const currentAwardIds = new Set(validCurrentAwards.map(a => a.id));
        const newAwards = [];

        for (const award of AWARDS) {
            if (!currentAwardIds.has(award.id)) {
                if (award.condition(null, currentStats)) {
                    newAwards.push({
                        id: award.id,
                        title: award.title,
                        description: award.description,
                        date: new Date().toISOString()
                    });
                }
            }
        }

        // 5. Update Supabase if needed (if new awards added OR old ones removed)
        if (newAwards.length > 0 || validCurrentAwards.length !== currentAwards.length) {
            const updatedAwards = [...validCurrentAwards, ...newAwards];

            const { error: updateError } = await supabase
                .from('users')
                .update({ awards: updatedAwards })
                .eq('id', userId);

            if (updateError) {
                console.error('Error updating awards:', updateError);
                return null;
            }

            return updatedAwards;
        }

    } catch (err) {
        console.error("Unexpected error in checkAndGrantAwards:", err);
    }

    return null; // No changes
}
